import React, { useEffect, useState } from "react"
import classNames from "classnames"
import chatCloudBg from "@/images/backgrounds/chat-bubble-bg.webp"
import "./ChatCloud.css"

interface ChatCloudProps {
  message: string
  username: string
  isVisible: boolean
  onHide: () => void
  playerOrder?: PlayerOrder
  /**
   * Which side of the card the bubble hangs off. "above" is for the viewer's own
   * card, which is pinned to the bottom of the left sidebar — a bubble below it
   * would sit on top of the trade button and the bank.
   */
  placement?: "above" | "below"
}

const ChatCloud: React.FC<ChatCloudProps> = ({
  message,
  username,
  isVisible,
  onHide,
  playerOrder,
  placement = "below",
}) => {
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true)
      const timer = setTimeout(() => {
        setIsAnimating(false)
        setTimeout(onHide, 300) // Wait for fade out animation
      }, 4000)
      return () => clearTimeout(timer)
    }
  }, [isVisible, onHide])

  if (!isVisible) return null

  // Determine positioning based on player order
  const getPositionClasses = () => {
    if (placement === "above") {
      return "absolute bottom-full -mb-4 left-[10%] z-[9999]"
    }
    switch (playerOrder) {
      case 0: // Top player - show below, move towards board
        return "absolute top-full -mt-4 left-[10%]   z-[9999]"
      case 1: // Right player - show below, move towards board
        return "absolute top-full -mt-4 left-[10%]   z-[9999]"
      case 2: // Bottom player - show below, move towards board
        return "absolute top-full -mt-4 left-[10%]   z-[9999]"
      case 3: // Left player - show below, move towards board
        return "absolute top-full -mt-4 left-[10%]  z-[9999]"
      default:
        return "absolute top-full mt-8 left-1/4 transform -translate-x-1/2 z-[9999]"
    }
  }

  return (
    <div
      className={classNames(
        getPositionClasses(),
        "z-[9999] chat-container chat-hero",
        {
          "chat-hero-enter": isAnimating,
          "chat-hero-exit": !isAnimating,
        }
      )}
    >
      <div
        className={classNames(
          "relative inline-block bg-black text-gray-800 rounded-xl border border-gray-300 shadow-2xl px-8 py-6 w-full chat-bubble",
          {
            "chat-bob chat-glow-on": isAnimating,
          }
        )}
        style={{
          background: `url(${chatCloudBg})`,
          boxShadow: "0 0 10px 5px rgba(0, 0, 0, 0.8)",
        }}
      >
        {/* Tail, pointing back at the card the bubble belongs to. */}
        <svg
          width="30"
          height="24"
          viewBox="0 0 30 24"
          className={classNames("absolute left-6 pointer-events-none", {
            "-top-6": placement === "below",
            "-bottom-6": placement === "above",
          })}
        >
          <path
            d={placement === "above" ? "M1 0 L14 23 L29 0 Z" : "M1 24 L14 1 L29 24 Z"}
            fill="#ffffff"
            // stroke="#d1d5db"
            strokeWidth="2"
          />
        </svg>
        <p className="text-4xl font-bold leading-tight text-center no-wrap whitespace-nowrap chat-text-pop">
          {message}
        </p>
      </div>
      {isAnimating && (
        <div className="chat-sparkles">
          <span className="sparkle sparkle-1" />
          <span className="sparkle sparkle-2" />
          <span className="sparkle sparkle-3" />
          <span className="sparkle sparkle-4" />
          <span className="sparkle sparkle-5" />
          <span className="sparkle sparkle-6" />
        </div>
      )}
    </div>
  )
}

export default ChatCloud
